import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Avatar,
    Chip,
    IconButton,
    Stack,
    Divider,
    alpha,
    useTheme,
    Tooltip,
} from '@mui/material';
import {
    AccountCircle as AccountIcon,
    MoreVert as MoreIcon,
    WhatsApp as WhatsAppIcon,
    Instagram as InstagramIcon,
    Star as StarIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';
import { STATUS_CONFIG } from './CRMConstants';

const LeadCard = ({ lead, onMenuOpen }) => {
    const theme = useTheme();

    const isOverdue = (lead) => {
        if (['closed', 'won', 'lost'].includes(lead.status)) return false;
        if (!lead.followUpAt) return false;
        const d = lead.followUpAt?.toDate ? lead.followUpAt.toDate()
            : lead.followUpAt?.seconds ? new Date(lead.followUpAt.seconds * 1000)
                : new Date(lead.followUpAt);
        return !isNaN(d.getTime()) && d < new Date();
    };

    const overdue = isOverdue(lead);
    const config = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
    const StatusIcon = config.icon;

    // Helper to safely format Firestore Timestamps or regular dates
    const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        // Handle Firestore Timestamp { seconds, nanoseconds }
        if (dateValue.toDate && typeof dateValue.toDate === 'function') {
            return dateValue.toDate().toLocaleDateString();
        }
        // Handle direct seconds/nanoseconds object
        if (dateValue.seconds) {
            return new Date(dateValue.seconds * 1000).toLocaleDateString();
        }
        // Fallback for string or Date object
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
    };

    const handleInstagramClick = () => {
        if (lead.userProfileUrl) {
            window.open(lead.userProfileUrl, '_blank');
        } else if (lead.username) {
            window.open(`https://instagram.com/${lead.username}`, '_blank');
        } else {
            alert('No Instagram profile available for this lead.');
        }
    };

    const handleWhatsAppClick = () => {
        // Future: implement WhatsApp Web link if phone number is captured
        alert('WhatsApp contact integration coming soon!');
    };

    return (
        <Card sx={{
            borderRadius: 4,
            border: `1px solid ${overdue ? alpha(theme.palette.error.main, 0.2) : alpha(theme.palette.divider, 0.05)}`,
            transition: 'all 0.3s ease',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 24px -10px ${alpha(theme.palette.common.black, 0.15)}`,
                bgcolor: alpha(theme.palette.background.paper, 0.9)
            },
            position: 'relative',
            overflow: 'visible'
        }}>
            {overdue && (
                <Box sx={{
                    position: 'absolute',
                    top: -10,
                    right: 20,
                    bgcolor: 'error.main',
                    color: 'white',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    fontSize: '0.65rem',
                    fontWeight: 900,
                    boxShadow: 3,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                }}>
                    <ErrorIcon sx={{ fontSize: '0.8rem' }} /> OVERDUE
                </Box>
            )}

            {lead.is_high_value && (
                <Box sx={{
                    position: 'absolute',
                    top: -10,
                    left: 20,
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    fontSize: '0.65rem',
                    fontWeight: 900,
                    boxShadow: 3,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                }}>
                    <StarIcon sx={{ fontSize: '0.8rem' }} /> HIGH VALUE
                </Box>
            )}

            <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Avatar sx={{
                            bgcolor: alpha(theme.palette[config.color].main, 0.1),
                            color: `${config.color}.main`,
                            width: 44,
                            height: 44
                        }}>
                            <AccountIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                {lead.username ? `@${lead.username}` : 'Potential Customer'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                via {lead.platform ? (lead.platform.charAt(0).toUpperCase() + lead.platform.slice(1)) : 'Instagram'} • {formatDate(lead.createdAt)}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton size="small" onClick={(e) => onMenuOpen(e, lead)}>
                        <MoreIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Typography variant="body2" sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: alpha(theme.palette.text.primary, 0.02),
                    borderRadius: 3,
                    fontStyle: 'italic',
                    borderLeft: `4px solid ${theme.palette[config.color].main}`
                }}>
                    "{lead.comment_text}"
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                        icon={<StatusIcon overdue={overdue} />}
                        label={config.label}
                        color={config.color}
                        size="small"
                        sx={{ fontWeight: 700, borderRadius: 1.5 }}
                    />
                    {lead.followUpAt && (
                        <Chip
                            label={`Follow up: ${formatDate(lead.followUpAt)}`}
                            variant="outlined"
                            color={overdue ? 'error' : 'default'}
                            size="small"
                            sx={{ fontWeight: 600, borderRadius: 1.5 }}
                        />
                    )}
                    {lead.deal_value > 0 && (
                        <Chip
                            label={`$${lead.deal_value}`}
                            color="success"
                            size="small"
                            sx={{ fontWeight: 800, borderRadius: 1.5 }}
                        />
                    )}
                </Stack>

                <Divider sx={{ my: 2, borderStyle: 'dashed' }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{
                            color: lead.reply_status?.startsWith('failed') ? 'error.main' : 'primary.main',
                            fontWeight: 700,
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            AI {lead.reply_status === 'success' ? 'Replied' : lead.reply_status?.startsWith('failed') ? 'Fail' : 'Draft'}: {String(lead.ai_response || 'No reply').substring(0, 30)}...
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Instagram DM">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={handleInstagramClick}
                                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                            >
                                <InstagramIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="WhatsApp">
                            <IconButton
                                size="small"
                                color="success"
                                onClick={handleWhatsAppClick}
                                sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}
                            >
                                <WhatsAppIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>
            </CardContent>
        </Card>
    );
};

export default LeadCard;
